// src/admin/pages/CampaignDetailPage.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Stack,
  TextField,
  Button,
  Alert,
  ImageList,
  ImageListItem,
  IconButton,
  LinearProgress,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/client";
import {
  ADMIN_CAMPAIGNS_QUERY,
  UPDATE_CAMPAIGN_MUTATION,
  DELETE_CAMPAIGN_MUTATION,
} from "../../graphql/campaigns";
import { uploadCampaignImages } from "../../api/campaignImages";
import AdminLoading from "../Components/AdminLoading";
import { buildImageUrl } from "../../config";

type Campaign = {
  id: string;
  name: string;
  images: string[];
};

const MAX_FILES_PER_UPLOAD = 10;
const STUCK_WARNING_MS = 8000;

const CampaignDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data, loading, error } = useQuery(ADMIN_CAMPAIGNS_QUERY);
  const [updateCampaign, { loading: updating }] = useMutation(
    UPDATE_CAMPAIGN_MUTATION,
    { refetchQueries: [{ query: ADMIN_CAMPAIGNS_QUERY }] },
  );
  const [deleteCampaign, { loading: deleting }] = useMutation(
    DELETE_CAMPAIGN_MUTATION,
    { refetchQueries: [{ query: ADMIN_CAMPAIGNS_QUERY }] },
  );

  const [name, setName] = useState("");
  const [images, setImages] = useState<string[]>([]);
  const [actionError, setActionError] = useState<string | null>(null);
  const [savingName, setSavingName] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [uploadProgress, setUploadProgress] = useState<{
    done: number;
    total: number;
  } | null>(null);
  const [takingLonger, setTakingLonger] = useState(false);
  const lastProgressRef = useRef<number>(0);
  const stuckTimerRef = useRef<number | null>(null);

  const campaign: Campaign | undefined = useMemo(() => {
    const list: Campaign[] = data?.campaigns || [];
    return list.find((c) => c.id === id);
  }, [data, id]);

  useEffect(() => {
    if (campaign) {
      setName(campaign.name);
      setImages(campaign.images || []);
    }
  }, [campaign]);

  const busy = loading || updating || deleting || savingName || uploading;

  const handleSaveName = async () => {
    if (!campaign || !id) return;
    if (!name.trim()) {
      setActionError("Name is required");
      return;
    }
    setActionError(null);
    setSavingName(true);
    try {
      await updateCampaign({
        variables: { id, name: name.trim(), images },
      });
    } catch (err: any) {
      setActionError(err.message);
    } finally {
      setSavingName(false);
    }
  };

  const clearStuckTimer = () => {
    if (stuckTimerRef.current) {
      window.clearInterval(stuckTimerRef.current);
      stuckTimerRef.current = null;
    }
  };

  const startStuckTimer = () => {
    clearStuckTimer();
    setTakingLonger(false);
    lastProgressRef.current = 0;

    stuckTimerRef.current = window.setInterval(() => {
      // If no progress change for STUCK_WARNING_MS, show message
      const nowProgress = uploadProgress?.done ?? 0;
      if (nowProgress === lastProgressRef.current) {
        setTakingLonger(true);
      } else {
        lastProgressRef.current = nowProgress;
        setTakingLonger(false);
      }
    }, STUCK_WARNING_MS);
  };

  const handleAddImages = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!campaign || !id) return;
    if (!e.target.files) return;

    const selected = Array.from(e.target.files);
    e.target.value = ""; // reset input early so same files can be selected again

    if (selected.length === 0) return;

    // ✅ limit: max 10 at a time
    if (selected.length > MAX_FILES_PER_UPLOAD) {
      setActionError(
        `You can upload up to ${MAX_FILES_PER_UPLOAD} images at a time.`,
      );
      return;
    }

    setActionError(null);
    setUploading(true);
    setUploadProgress({ done: 0, total: selected.length });
    setTakingLonger(false);
    lastProgressRef.current = 0;

    // Start stuck watcher (if progress doesn't move)
    clearStuckTimer();
    stuckTimerRef.current = window.setInterval(() => {
      setUploadProgress((p) => {
        const done = p?.done ?? 0;
        if (done === lastProgressRef.current) {
          setTakingLonger(true);
        } else {
          lastProgressRef.current = done;
          setTakingLonger(false);
        }
        return p;
      });
    }, STUCK_WARNING_MS);

    try {
      const uploadedPaths = await uploadCampaignImages(
        selected,
        (done, total) => {
          setUploadProgress({ done, total });
          if (done > 0) setTakingLonger(false); // once it moves, hide warning
        },
      );

      const newImages = [...images, ...uploadedPaths];

      await updateCampaign({
        variables: {
          id,
          name: name.trim() || campaign.name,
          images: newImages,
        },
      });

      setImages(newImages);
      setUploadProgress(null);
      setTakingLonger(false);
    } catch (err: any) {
      setActionError(err.message || "Failed to upload images");
      setUploadProgress(null);
      setTakingLonger(false);
    } finally {
      clearStuckTimer();
      setUploading(false);
    }
  };

  const handleDeleteImage = async (idx: number) => {
    if (!campaign || !id) return;

    const newImages = images.filter((_, i) => i !== idx);
    setActionError(null);

    try {
      await updateCampaign({
        variables: {
          id,
          name: name.trim() || campaign.name,
          images: newImages,
        },
      });
      setImages(newImages);
    } catch (err: any) {
      setActionError(err.message);
    }
  };

  const handleRemoveAllImages = async () => {
    if (!campaign || !id) return;
    if (images.length === 0) return;

    const ok = window.confirm(
      "Are you sure you want to remove all images from this campaign?",
    );
    if (!ok) return;

    setActionError(null);
    try {
      await updateCampaign({
        variables: { id, name: name.trim() || campaign.name, images: [] },
      });
      setImages([]);
    } catch (err: any) {
      setActionError(err.message);
    }
  };

  const handleDeleteCampaign = async () => {
    if (!campaign || !id) return;

    const ok = window.confirm(
      `Are you sure you want to delete campaign "${campaign.name}"?`,
    );
    if (!ok) return;

    setActionError(null);
    try {
      await deleteCampaign({ variables: { id } });
      navigate("/admin/campaigns");
    } catch (err: any) {
      setActionError(err.message);
    }
  };

  if (loading && !campaign) {
    return <AdminLoading open />;
  }

  if (error) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/admin/campaigns")}
          sx={{ mb: 2 }}
        >
          Back to Campaigns
        </Button>
        <Alert severity="error">
          Failed to load campaigns: {error.message}
        </Alert>
      </Box>
    );
  }

  if (!campaign) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/admin/campaigns")}
          sx={{ mb: 2 }}
        >
          Back to Campaigns
        </Button>
        <Alert severity="warning">
          Campaign not found. It may have been deleted.
        </Alert>
      </Box>
    );
  }

  const progressPercent =
    uploadProgress && uploadProgress.total > 0
      ? (uploadProgress.done / uploadProgress.total) * 100
      : 0;

  return (
    <Box>
      <AdminLoading open={busy} />
      <Stack direction="row" alignItems="center" spacing={2} mb={2}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/admin/campaigns")}
        >
          Back
        </Button>
        <Typography variant="h5">Campaign Details</Typography>
      </Stack>

      {actionError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {actionError}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" color="text.secondary" mb={1}>
          Campaign Name
        </Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <TextField
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <Button
            variant="contained"
            onClick={handleSaveName}
            disabled={savingName}
          >
            {savingName ? "Saving..." : "Save Name"}
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={handleDeleteCampaign}
            disabled={deleting}
          >
            {deleting ? "Deleting..." : "Delete Campaign"}
          </Button>
        </Stack>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          mb={2}
          spacing={2}
        >
          <Typography variant="subtitle2" color="text.secondary">
            Images ({images.length})
          </Typography>

          <Stack direction="row" spacing={2}>
            <Button variant="outlined" component="label" disabled={uploading}>
              {uploading
                ? "Uploading..."
                : `Add Images (max ${MAX_FILES_PER_UPLOAD})`}
              <input
                type="file"
                hidden
                multiple
                accept="image/*"
                onChange={handleAddImages}
              />
            </Button>
            <Button
              variant="outlined"
              color="error"
              disabled={images.length === 0 || uploading}
              onClick={handleRemoveAllImages}
            >
              Remove All
            </Button>
          </Stack>
        </Stack>

        {/* ✅ Progress + Stuck message (no layout redesign) */}
        {uploadProgress && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Uploading images: {uploadProgress.done}/{uploadProgress.total}
            </Typography>
            <LinearProgress variant="determinate" value={progressPercent} />
            {takingLonger && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Taking longer than usual… This can happen on slow internet or
                with large images.
              </Typography>
            )}
          </Box>
        )}

        {images.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No images uploaded yet.
          </Typography>
        ) : (
          <ImageList cols={3} gap={8} sx={{ maxHeight: "353px" }}>
            {images.map((src, idx) => (
              <ImageListItem key={src + idx}>
                <Box
                  sx={{
                    position: "relative",
                    borderRadius: 1,
                    overflow: "hidden",
                    border: "1px solid rgba(0,0,0,0.1)",
                  }}
                >
                  <Box
                    component="img"
                    loading="lazy"
                    decoding="async"
                    src={buildImageUrl(src)}
                    alt={`${campaign.name} ${idx + 1}`}
                    sx={{
                      width: "100%",
                      height: 200,
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                  <IconButton
                    size="small"
                    sx={{
                      position: "absolute",
                      top: 4,
                      right: 4,
                      bgcolor: "rgba(0,0,0,0.6)",
                      color: "#fff",
                      "&:hover": { bgcolor: "rgba(0,0,0,0.8)" },
                    }}
                    onClick={() => handleDeleteImage(idx)}
                    disabled={uploading}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </ImageListItem>
            ))}
          </ImageList>
        )}
      </Paper>
    </Box>
  );
};

export default CampaignDetailPage;
